// src/pages/roles/RolesPage.jsx - Translated version
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Alert,
  Tooltip,
  Avatar,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Security as SecurityIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  People as PeopleIcon,
  VpnKey as VpnKeyIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import LocalizedDateDistance from '../../../i18n/LocalizedDateDistance';

// Hooks
import { useRoles } from '../hooks/useRoles';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import { useConfirmDialog } from '../../../hooks/mui/useConfirmDialog';
import { usePermissions } from '../../auth/hooks/usePermissions';

// Utils
import { UUIDDisplay } from '../../../utils/formatters/uuid';
import { capitalize } from '../../../utils/formatters/capitalize';

import PageHeader from '../../../components/common/PageHeader';

export default function RolesPage() {
  const navigate = useNavigate();
  const { t } = useTranslation(['roles', 'common']);
  const { showSuccess, showError } = useSnackbar();
  const { showConfirmDialog, ConfirmDialog } = useConfirmDialog();
  const permissions = usePermissions();

  // State
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  // API hooks
  const {
    roles,
    loading,
    error,
    clearError,
    fetchDepartmentRoles,
    deleteRole,
  } = useRoles();

  // Load initial data
  useEffect(() => {
    fetchDepartmentRoles();
  }, [fetchDepartmentRoles]);

  // Event handlers
  const handleRefresh = useCallback(() => {
    fetchDepartmentRoles();
  }, [fetchDepartmentRoles]);

  const handleCreateClick = useCallback(() => {
    navigate('/roles/create');
  }, [navigate]);

  const handleViewRole = useCallback((role) => {
    navigate(`/roles/${role.role_id}`);
  }, [navigate]);

  const handleEditRole = useCallback((role) => {
    navigate(`/roles/${role.role_id}?mode=edit`);
  }, [navigate]);

  const handleMenuOpen = useCallback((event, role) => {
    event.stopPropagation();
    setSelectedRole(role);
    setActionMenuAnchor(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setActionMenuAnchor(null);
    setSelectedRole(null);
  }, []);

  const handleDeleteRole = useCallback((role) => {
    showConfirmDialog({
      title: t('roles:confirm.delete_role_title'),
      message: t('roles:confirm.delete_role_message', { roleName: role.name }),
      confirmText: t('roles:confirm.delete_confirm'),
      severity: 'error',
      onConfirm: async () => {
        try {
          const result = await deleteRole(role.role_id);
          if (result.success) {
            showSuccess(t('roles:success.role_deleted'));
          } else {
            showError(result.error || t('roles:errors.delete_failed'));
          }
        } catch (error) {
          showError(t('roles:errors.delete_failed'));
        }
      },
    });
    handleMenuClose();
  }, [showConfirmDialog, deleteRole, showSuccess, showError, handleMenuClose, t]);

  // Check permissions
  const canCreate = permissions.hasScope('role:create');
  const canUpdate = permissions.hasScope('role:update');
  const canDelete = permissions.hasScope('role:delete');

  // DataGrid columns
  const columns = useMemo(() => [
    {
      field: 'name',
      headerName: t('roles:labels.name'),
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={2} sx={{ py: 1 }}>
          <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
            <SecurityIcon fontSize="small" />
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight="medium">
              {capitalize(params.value)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t("common:labels.id")} <UUIDDisplay uuid={params.row.role_id} />
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'description',
      headerName: t('roles:labels.description'),
      flex: 2,
      minWidth: 250,
      renderCell: (params) => (
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ 
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            lineHeight: 1.4,
            py: 1,
          }}
        >
          {params.value || t('common:messages.no_data')}
        </Typography>
      ),
    },
    {
      field: 'staff_count',
      headerName: t('roles:staff_members'),
      width: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1}>
          <PeopleIcon fontSize="small" color="action" />
          <Typography variant="body2">
            {params.value || 0}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'permissions_count',
      headerName: t('roles:permissions'),
      width: 140,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={1}>
          <VpnKeyIcon fontSize="small" color="action" />
          <Typography variant="body2">
            {params.value || 0}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'created_at',
      headerName: t('roles:labels.created'),
      width: 140,
      renderCell: (params) => (
        <Typography variant="caption" color="text.secondary">
          {params.value ? <LocalizedDateDistance date={params.value} /> : t('common:messages.no_data')}
        </Typography>
      ),
    },
    {
      field: 'actions',
      // Icon-only kebab column: no header, like the other list grids
      headerName: '',
      width: 80,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <IconButton
          size="small"
          onClick={(e) => handleMenuOpen(e, params.row)}
        >
          <MoreVertIcon />
        </IconButton>
      ),
    },
  ], [handleMenuOpen, t]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      {/* Header */}
      <PageHeader
        icon={SecurityIcon}
        title={t('roles:title')}
        subtitle={t('roles:subtitle')}
        actions={<>
          <Tooltip title={t('roles:actions.refresh')}>
            <IconButton onClick={handleRefresh} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>

          {canCreate && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateClick}
              disabled={loading}
            >
              {t('roles:create_role')}
            </Button>
          )}
        </>}
      />

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={clearError}>
          {error}
        </Alert>
      )}

      {/* DataGrid */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          width: '100%',
          '& .MuiDataGrid-root': {
            border: 'none',
            '& .MuiDataGrid-main': {
              '& .MuiDataGrid-columnHeaders': {
                borderBottom: '1px solid',
                borderColor: 'divider',
                backgroundColor: 'background.paper',
              },
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                '&:focus': {
                  outline: 'none',
                },
              },
              '& .MuiDataGrid-row': {
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              },
            },
          },
        }}
      >
        <DataGrid
          rows={roles}
          columns={columns}
          getRowId={(row) => row.role_id}
          loading={loading}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[5, 10, 25, 50]}
          disableRowSelectionOnClick
          disableColumnMenu
          onRowClick={(params) => handleViewRole(params.row)}
          rowHeight={72}
          getRowHeight={() => 'auto'}
          slots={{
            noRowsOverlay: () => (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                height="100%"
                py={4}
              >
                <SecurityIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {t('roles:messages.no_roles')}
                </Typography>
                <Typography variant="body2" color="text.secondary" align="center" paragraph>
                  {t('roles:messages.no_roles_description')}
                </Typography>
                {canCreate && (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleCreateClick}
                  >
                    {t('roles:messages.create_first_role')}
                  </Button>
                )}
              </Box>
            ),
          }}
          sx={{
            backgroundColor: 'background.paper',
            borderRadius: 2,
            '& .MuiDataGrid-footerContainer': {
              borderTop: '1px solid',
              borderColor: 'divider',
            },
          }}
        />
      </Box>

      {/* Action Menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={Boolean(actionMenuAnchor)}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 3,
        }}
      >
        <MenuItem onClick={() => { handleViewRole(selectedRole); handleMenuClose(); }}>
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t('roles:actions.view_details')}</ListItemText>
        </MenuItem>

        {canUpdate && (
          <MenuItem onClick={() => { handleEditRole(selectedRole); handleMenuClose(); }}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t('roles:actions.edit')}</ListItemText>
          </MenuItem>
        )}

        {canDelete && (
          <MenuItem onClick={() => { handleDeleteRole(selectedRole); handleMenuClose(); }}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" color="error" />
            </ListItemIcon>
            <ListItemText>
              <Typography color="error">{t('roles:actions.delete')}</Typography>
            </ListItemText>
          </MenuItem>
        )}
      </Menu>
      <ConfirmDialog />
    </Box>
  );
}